import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Copy, Loader2, RefreshCw } from 'lucide-react';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/shared/StatusBadge';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import {
    useCampaignSubmission,
    useReprocessCampaignSubmission,
} from '@/api/campaigns/use-campaign-submissions';
import { META_AD_META_KEYS, type CampaignSubmission } from '@/api/campaigns/types';
import SubmissionLeadLink from './SubmissionLeadLink';
import { getMetaAdValue, canReprocessSubmission } from '@/lib/campaignSubmission';

export interface SubmissionDetailsDialogProps {
    campaignId: string;
    /** Facebook campaigns show the Meta ad attribution fields. */
    isFacebook?: boolean;
    /** Rendered open whenever non-null; closing sets this back to null in the parent. */
    submission: CampaignSubmission | null;
    crmTimezone: string;
    /** CAMPAIGNS edit permission — gates the reprocess button. */
    canEdit: boolean;
    onOpenChange: (open: boolean) => void;
}

/** Pretty JSON for the tab panes, or null for an empty/absent value (renders the empty hint instead). */
function formatJson(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object' && Object.keys(value as object).length === 0) return null;
    return JSON.stringify(value, null, 2);
}

const JsonPane: React.FC<{
    json: string | null;
    emptyLabel: string;
    copyLabel: string;
    onCopy: (value: string) => void;
}> = ({ json, emptyLabel, copyLabel, onCopy }) => {
    if (!json) {
        return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
    }
    return (
        <div className="relative">
            <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-2"
                onClick={() => onCopy(json)}
            >
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                {copyLabel}
            </Button>
            <pre className="max-h-80 overflow-auto rounded-md border bg-muted/40 p-3 pt-12 text-xs">
                {json}
            </pre>
        </div>
    );
};

/**
 * Submission detail "drawer" (quantix-api/docs/campaigns/TASKS.md, W4). There's no
 * dedicated Sheet/Drawer primitive in this codebase yet, so this is a wide Dialog
 * with tabs — raw payload / meta / mapped data / warnings & errors — plus a
 * reprocess action for failed/pending submissions.
 */
const SubmissionDetailsDialog: React.FC<SubmissionDetailsDialogProps> = ({
    campaignId,
    isFacebook = false,
    submission: listRow,
    crmTimezone,
    canEdit,
    onOpenChange,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const reprocessMutation = useReprocessCampaignSubmission();

    // The list endpoint strips raw_payload, so load the full submission while open and
    // use the list row as placeholder. Reprocess invalidates this query (same key prefix).
    const detailQuery = useCampaignSubmission(campaignId, listRow?.id ?? '');
    const submission: CampaignSubmission | null = listRow
        ? { ...listRow, ...(detailQuery.data?.data ?? {}) }
        : null;
    const rawLoading = detailQuery.isLoading;

    const copy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar(t('campaigns.detail.drawer.copied'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.detail.drawer.copyFailed'), { variant: 'error' });
        }
    };

    const handleReprocess = async () => {
        if (!submission) return;
        try {
            await reprocessMutation.mutateAsync({ campaignId, submissionId: submission.id });
            enqueueSnackbar(t('campaigns.detail.drawer.reprocessed'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.detail.drawer.reprocessFailed'), { variant: 'error' });
        }
    };

    const rawJson = submission ? formatJson(submission.raw_payload) : null;
    const metaJson = submission ? formatJson(submission.meta) : null;
    const mappedJson = submission ? formatJson(submission.mapped_data) : null;
    const warnings = submission?.warnings ?? [];
    const errors = submission?.errors ?? [];
    const metaAdEntries = submission
        ? META_AD_META_KEYS.map(key => ({ key, value: getMetaAdValue(submission, key) })).filter(
              entry => entry.value !== null
          )
        : [];
    const issueCount = warnings.length + errors.length;

    return (
        <Dialog open={!!submission} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex flex-wrap items-center gap-2">
                        {t('campaigns.detail.drawer.title')}
                        {submission && <StatusBadge status={submission.status} kind="submission" />}
                        {submission?.is_test && (
                            <Badge variant="outline">
                                {t('campaigns.detail.submissions.testBadge')}
                            </Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {submission && (
                    <DialogBody className="space-y-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <p className="text-muted-foreground">
                                    {t('campaigns.detail.submissions.columns.receivedAt')}
                                </p>
                                <DateTimeFormatter
                                    date={submission.received_at}
                                    timeZone={crmTimezone}
                                />
                            </div>
                            <div>
                                <p className="text-muted-foreground">
                                    {t('campaigns.detail.submissions.columns.lead')}
                                </p>
                                <SubmissionLeadLink submission={submission} />
                            </div>
                        </div>

                        {isFacebook && metaAdEntries.length > 0 && (
                            <div>
                                <p className="mb-2 text-sm font-medium">
                                    {t('campaigns.metaAd.title')}
                                </p>
                                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                                    {metaAdEntries.map(({ key, value }) => (
                                        <div key={key}>
                                            <dt className="text-muted-foreground">
                                                {t(`campaigns.metaAd.fields.${key}`)}
                                            </dt>
                                            <dd className="break-words font-medium">{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        <Tabs defaultValue="raw">
                            <TabsList>
                                <TabsTrigger value="raw">
                                    {t('campaigns.detail.drawer.tabs.raw')}
                                </TabsTrigger>
                                <TabsTrigger value="meta">
                                    {t('campaigns.detail.drawer.tabs.meta')}
                                </TabsTrigger>
                                <TabsTrigger value="mapped">
                                    {t('campaigns.detail.drawer.tabs.mapped')}
                                </TabsTrigger>
                                <TabsTrigger value="issues" className="gap-1.5">
                                    {t('campaigns.detail.drawer.tabs.issues')}
                                    {issueCount > 0 && (
                                        <Badge variant="secondary">{issueCount}</Badge>
                                    )}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="raw">
                                {rawLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {t('campaigns.detail.drawer.loading')}
                                    </div>
                                ) : (
                                    <JsonPane
                                        json={rawJson}
                                        onCopy={copy}
                                        emptyLabel={t('campaigns.detail.drawer.empty')}
                                        copyLabel={t('campaigns.detail.drawer.copy')}
                                    />
                                )}
                            </TabsContent>
                            <TabsContent value="meta">
                                <JsonPane
                                    json={metaJson}
                                    onCopy={copy}
                                    emptyLabel={t('campaigns.detail.drawer.empty')}
                                    copyLabel={t('campaigns.detail.drawer.copy')}
                                />
                            </TabsContent>
                            <TabsContent value="mapped">
                                <JsonPane
                                    json={mappedJson}
                                    onCopy={copy}
                                    emptyLabel={t('campaigns.detail.drawer.empty')}
                                    copyLabel={t('campaigns.detail.drawer.copy')}
                                />
                            </TabsContent>
                            <TabsContent value="issues" className="space-y-4">
                                <div>
                                    <p className="mb-1 text-sm font-medium">
                                        {t('campaigns.detail.drawer.errorsTitle')}
                                    </p>
                                    {errors.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            {t('campaigns.detail.drawer.noErrors')}
                                        </p>
                                    ) : (
                                        <ul className="space-y-1">
                                            {errors.map((error, index) => (
                                                <li
                                                    key={index}
                                                    className="text-sm text-destructive"
                                                >
                                                    • {error}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div>
                                    <p className="mb-1 text-sm font-medium">
                                        {t('campaigns.detail.drawer.warningsTitle')}
                                    </p>
                                    {warnings.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            {t('campaigns.detail.drawer.noWarnings')}
                                        </p>
                                    ) : (
                                        <ul className="space-y-1">
                                            {warnings.map((warning, index) => (
                                                <li key={index} className="text-sm text-warning">
                                                    • {warning}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </DialogBody>
                )}

                <DialogFooter>
                    {submission && canEdit && canReprocessSubmission(submission.status) && (
                        <Button onClick={handleReprocess} disabled={reprocessMutation.isPending}>
                            {reprocessMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            {t('campaigns.detail.drawer.reprocess')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SubmissionDetailsDialog;
